import {
  AfterContentInit,
  Component,
  ContentChild,
  ElementRef,
  HostBinding,
  Input,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { ERRORS_KEYS } from './../error/error.component';
import { InputComponent } from './../input/input.component';
import { UnitType } from '../../pipes/unit.pipe';
import { isNaN, parseInt } from 'lodash';

@Component({
    selector: 'gl-input-container',
    templateUrl: './input-container.component.html',
    styleUrls: ['./input-container.component.scss'],
    standalone: false
})
export class InputContainerComponent implements AfterContentInit, OnInit {
  @Input() public errorBelowInput: boolean;
  @Input() public errorAboveInput: boolean;
  /** Already-translated API or custom message; shows like field validation (icon + tooltip) after submit. */
  @Input() public rawFieldError: string | null = null;
  @Input() public isErrorVisible = false;
  @Input() public units: UnitType;
  @HostBinding('class.whiteText') @Input() public whiteText: string;
  @ContentChild(NgControl, { static: true }) public control: NgControl;
  @ContentChild(NgControl, { static: true, read: ElementRef })
  public element: ElementRef<HTMLInputElement>;
  @ContentChild(InputComponent, { static: true }) public inputComponent: InputComponent;
  @ViewChild('placeholderEL', { static: true, read: ElementRef }) public placeholderEl: ElementRef;
  @ViewChild('errorIcon', { static: true, read: ElementRef }) public errorIconEl: ElementRef;
  public errorsKeys = ERRORS_KEYS;
  public isFocused: boolean;
  public isHovered: boolean;
  @HostBinding('class.disabled') public isDisabled: boolean;
  public wasPassedAsPasswordType: boolean;
  public isPasswordType: boolean;
  public value: any;
  public placeholder: string;
  public messageValue: number;


  constructor(private renderer: Renderer2, private elRef: ElementRef<HTMLElement>) {}

  public ngOnInit() {
    if (this.element.nativeElement.type === 'password') {
      this.wasPassedAsPasswordType = true;
      this.isPasswordType = true;
    }
  }

  public ngAfterContentInit() {
    this.value = this.element.nativeElement.value;
    this.placeholder = this.element.nativeElement.placeholder;
    this.isDisabled = this.element.nativeElement.disabled;
    this.inputComponent.setDisabledState = this.setDisabledState.bind(this);

    this.inputComponent.INPUT_ATTRIBUTES.forEach((attr) => {
      if (this.inputComponent.hasAttribute(attr)) {
        this.elRef.nativeElement.classList.add(attr);
      }
    });
    this.element.nativeElement.onblur = this.onBlur.bind(this);
    this.element.nativeElement.onfocus = this.onFocus.bind(this);
    this.elRef.nativeElement.onmouseenter = this.onMouseEnter.bind(this);
    this.elRef.nativeElement.onmouseleave = this.onMouseLeave.bind(this);
  }

  public minusSignHandler() {
    let value = parseInt(this.element.nativeElement.value, 10);
    value = isNaN(value) ? 0 : value;
    this.inputComponent.setValue(value - 1);
  }

  public plusSignHandler() {
    let value = parseInt(this.element.nativeElement.value, 10);
    value = isNaN(value) ? 0 : value;
    this.inputComponent.setValue(value + 1);
  }

  public onBlur() {
    this.isFocused = false;
    this.isHovered = false;
  }

  public onFocus() {
    this.isFocused = true;
  }

  public onMouseEnter() {
    this.isHovered = true;
  }

  public onMouseLeave() {
    this.isHovered = false;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.renderer.setProperty(this.element.nativeElement, 'disabled', isDisabled);
  }

  public changeTypeOnPasswordInput(): void {
    if (this.isPasswordType) {
      this.renderer.setProperty(this.element.nativeElement, 'type', 'text');
      this.isPasswordType = false;
    } else {
      this.renderer.setProperty(this.element.nativeElement, 'type', 'password');
      this.isPasswordType = true;
    }
  }

  public getErrorObjectFirstTranslationKey() {
    if (this.control?.errors) {
      if (!isNaN(+Object.values(this.control.errors)[0])) {
        this.messageValue = +Object.values(this.control.errors)[0];
      }
      return this.errorsKeys[Object.keys(this.control.errors)[0]] as string;
    }
  }

  public getTooltipErrorText(): string {
    if (this.rawFieldError) {
      return this.rawFieldError;
    }
    return (this.getErrorObjectFirstTranslationKey() as string) ?? '';
  }

  public showErrorIcon(): boolean {
    if (this.errorBelowInput || this.errorAboveInput) {
      return false;
    }
    if (this.rawFieldError && this.inputComponent?.submitted) {
      return true;
    }
    if (!this.control?.errors) {
      return false;
    }
    if (this.isErrorVisible && !this.control.errors['required']) {
      return true;
    }
    return Boolean(this.inputComponent?.submitted);
  }
}
